import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Mail, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { applyBrandingColors } from '../utils/branding';

function ForgotPassword() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [branding, setBranding] = useState({
        companyName: 'Sensity',
        companyLogo: null,
        primaryColor: '#2563eb'
    });

    // Load branding settings
    useEffect(() => {
        const loadBranding = async () => {
            try {
                // Try localStorage first
                const savedSettings = localStorage.getItem('appSettings');
                if (savedSettings) {
                    try {
                        const parsed = JSON.parse(savedSettings);
                        if (parsed.branding) {
                            setBranding(prev => ({ ...prev, ...parsed.branding }));
                        }
                    } catch (error) {
                        console.error('Error loading branding from localStorage:', error);
                    }
                }

                // Try to load from API
                const response = await apiService.getBrandingSettings();
                if (response?.branding) {
                    setBranding(prev => ({ ...prev, ...response.branding }));
                }
            } catch (error) {
                // Settings API might not be available
                console.warn('Could not load branding settings:', error);
            }
        };

        loadBranding();
    }, []);

    // Apply branding colors
    useEffect(() => {
        if (branding.primaryColor) {
            applyBrandingColors(branding.primaryColor);
        }
    }, [branding.primaryColor]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error(t('forgotPassword.errors.emailRequired'));
            return;
        }

        setLoading(true);
        try {
            await apiService.forgotPassword(email);
            setEmailSent(true);
            toast.success(t('forgotPassword.messages.emailSent'));
        } catch (error) {
            toast.error(t('forgotPassword.errors.sendFailed'));
        } finally {
            setLoading(false);
        }
    };

    if (emailSent) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="max-w-md w-full">
                    <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                        {/* Logo */}
                        {branding.companyLogo && (
                            <div className="mx-auto mb-4">
                                <img
                                    src={`${branding.companyLogo}?${Date.now()}`}
                                    alt="Company Logo"
                                    className="h-16 w-auto mx-auto object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        <div className="text-center">
                            <div
                                className="mx-auto h-12 w-12 flex items-center justify-center rounded-full mb-4"
                                style={{
                                    background: `linear-gradient(135deg, #10b98120, #10b98140)`
                                }}
                            >
                                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>

                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t('forgotPassword.success.title')}
                            </h2>
                            <p className="text-sm text-gray-600 mb-4">
                                <Trans
                                    i18nKey="forgotPassword.success.subtitle"
                                    values={{ email }}
                                    components={{ strong: <strong /> }}
                                />
                            </p>

                            <p className="text-sm text-gray-500 mb-6">
                                {t('forgotPassword.success.description')}
                            </p>

                            <Link
                                to="/login"
                                className="btn-primary inline-flex justify-center w-full mb-4"
                            >
                                {t('forgotPassword.success.cta')}
                            </Link>

                            <p className="text-sm text-gray-500">
                                {t('forgotPassword.success.retryPrompt')}{' '}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEmailSent(false);
                                        setEmail('');
                                    }}
                                    className="font-medium hover:underline"
                                    style={{ color: branding.primaryColor }}
                                >
                                    {t('forgotPassword.success.retryAction')}
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
                    <div className="text-center">
                        {/* Logo */}
                        {branding.companyLogo && (
                            <div className="mx-auto mb-4">
                                <img
                                    src={`${branding.companyLogo}?${Date.now()}`}
                                    alt="Company Logo"
                                    className="h-16 w-auto mx-auto object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        {/* Fallback icon if no logo */}
                        {!branding.companyLogo && (
                            <div
                                className="mx-auto h-16 w-16 mb-4 flex items-center justify-center rounded-full"
                                style={{
                                    background: `linear-gradient(135deg, ${branding.primaryColor}20, ${branding.primaryColor}40)`
                                }}
                            >
                                <Lock className="h-8 w-8" style={{ color: branding.primaryColor }} />
                            </div>
                        )}

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {t('forgotPassword.form.title')}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {t('forgotPassword.form.subtitle')}
                        </p>
                    </div>

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('forgotPassword.form.emailLabel')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-field pl-10"
                                    placeholder={t('forgotPassword.form.emailPlaceholder')}
                                />
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full flex justify-center py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading && (
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {t('forgotPassword.form.submit')}
                            </button>
                        </div>

                        <div className="text-center">
                            <Link
                                to="/login"
                                className="text-sm font-medium hover:underline"
                                style={{ color: branding.primaryColor }}
                            >
                                {t('forgotPassword.form.backToLogin')}
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ForgotPassword;
